import { useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import { Toast } from '@base-ui/react/toast';
import { useRouter } from '@/navigation';

import { ApiBackendSingleResponse } from '@nf/types/api';
import { ToastStyle } from '@/components/toast/toast.types';
import { 
	TaskTypeEnum, 
	ClickTaskTypeEnum, 
	WcPromotionSpinDailyClickTask,
	WcPromotionSpinDailyBetTask,
	WcPromotionSpinDailyTask,
	WcPromotionSpinDailyInfoResponse,
	WcMissionPayload,
	WcSpinCompletedTask,
	WcPromotionSpinTasksCompleteResponse
} from '../types';
import { Maybe } from '@nf/utils-common/fpw/monadic';
import queryKeys from '@/constants/query-keys';
import { WC_PROMOTION_ELIGIBLE_BET_TYPES } from '../constants';
import { wcPromotionSpinService } from '../services/wc-promotion-spin.service';
import { useApiBackendInfo } from '@/hooks/query/use-api-backend-info';
import { useLicSettings } from '../../config/hooks/use-lic-settings';
import { useWcPromotionEligibility } from './use-wc-promotion-eligibility';
import { useWcPromotionSpinTasksComplete } from './use-wc-promotion-spin-tasks-complete';
import { KafkaNote } from '@nf/utils-common/constants';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';

type CompletableTaskTypeResolver = (
	params: WcMissionPayload,
	missions: WcPromotionSpinDailyTask[]
) => Array<WcSpinCompletedTask> | null;

export const SPORT_TYPE_TO_CLICK_TASK_TYPE: Record<number, ClickTaskTypeEnum> = {
	997: ClickTaskTypeEnum.SabaSoccerVirtual,
	9994: ClickTaskTypeEnum.SabaSoccerMarble,
	9995: ClickTaskTypeEnum.SabaSoccerPinGoal,
};

const PRODUCT_CODE_TO_CLICK_TASK_TYPE: Record<string, ClickTaskTypeEnum> = {
	'virtualSports': ClickTaskTypeEnum.VirtualSports,
	'casinoRoyale': ClickTaskTypeEnum.CasinoRoyale,
	'numberGame': ClickTaskTypeEnum.NumberGame,
	'happy5': ClickTaskTypeEnum.Happy5,
	'keno': ClickTaskTypeEnum.RngKeno,
	'kenoLottery': ClickTaskTypeEnum.Lottery,
	'vGaming': ClickTaskTypeEnum.Vgaming,
	'bet-builder': ClickTaskTypeEnum.BBPlus,
	'live-match': ClickTaskTypeEnum.LiveMatchIcon,
	'parlay': ClickTaskTypeEnum.ParlayButton,
	'smp': ClickTaskTypeEnum.SmpIcon,
	'streamer': ClickTaskTypeEnum.StreamerIcon,
	'streaming': ClickTaskTypeEnum.StreamingIcon,
	'time-machine': ClickTaskTypeEnum.SabaTimeMachine,
};

const getClickTaskToastContentMap = (
	t: ReturnType<typeof useTranslations<'General'>>
): Record<number, string> => ({
	[ClickTaskTypeEnum.SabaSoccerVirtual]: t('lbl_26WCSpinMissionC01'),
	[ClickTaskTypeEnum.SabaSoccerPinGoal]: t('lbl_26WCSpinMissionC02'),
	[ClickTaskTypeEnum.SabaSoccerMarble]: t('lbl_26WCSpinMissionC03'),
	[ClickTaskTypeEnum.VirtualSports]: t('lbl_26WCSpinMissionC04'),
	[ClickTaskTypeEnum.CasinoRoyale]: t('lbl_26WCSpinMissionC05'),
	[ClickTaskTypeEnum.NumberGame]: t('lbl_26WCSpinMissionC06'),
	[ClickTaskTypeEnum.Happy5]: t('lbl_26WCSpinMissionC07'),
	[ClickTaskTypeEnum.RngKeno]: t('lbl_26WCSpinMissionC08'),
	[ClickTaskTypeEnum.Lottery]: t('lbl_26WCSpinMissionC09'),
	[ClickTaskTypeEnum.Vgaming]: t('lbl_26WCSpinMissionC10'),
	[ClickTaskTypeEnum.StreamerIcon]: t('lbl_26WCSpinMissionD01'),
	[ClickTaskTypeEnum.StreamingIcon]: t('lbl_26WCSpinMissionD02'),
	[ClickTaskTypeEnum.LiveMatchIcon]: t('lbl_26WCSpinMissionD03'),
	[ClickTaskTypeEnum.SmpIcon]: t('lbl_26WCSpinMissionD04'),
	[ClickTaskTypeEnum.SabaTimeMachine]: t('lbl_26WCSpinMissionD05'),
	[ClickTaskTypeEnum.BBPlus]: t('lbl_26WCSpinMissionD07'),
	[ClickTaskTypeEnum.ParlayButton]: t('lbl_26WCSpinMissionD08'),
});

const getBetTaskToastContentMap = (
	t: ReturnType<typeof useTranslations<'General'>>
): Record<number, string> => ({
	1: t('lbl_26WCSpinMissionA01Brief'),
	3: t('lbl_26WCSpinMissionA02Brief'),
	6: t('lbl_26WCSpinMissionB03Brief'),
	7: t('lbl_26WCSpinMissionA03Brief'),
	8: t('lbl_26WCSpinMissionA04Brief'),
	29: t('lbl_26WCSpinMissionB10Brief'),
	159: t('lbl_26WCSpinMissionB04Brief'),
	161: t('lbl_26WCSpinMissionB08Brief'),
	162: t('lbl_26WCSpinMissionB09Brief'),
	412: t('lbl_26WCSpinMissionB05Brief'),
	413: t('lbl_26WCSpinMissionB01Brief'),
	414: t('lbl_26WCSpinMissionB02Brief'),
	417: t('lbl_26WCSpinMissionB06Brief'),
	448: t('lbl_26WCSpinMissionB07Brief'),
});

const resolveClickTaskTypes: CompletableTaskTypeResolver = ({ productCode, sportType }, missions) => {
	let clickTaskType: ClickTaskTypeEnum | undefined;

	if (productCode) {
		clickTaskType = PRODUCT_CODE_TO_CLICK_TASK_TYPE[productCode];
	} else if (sportType !== undefined) {
		clickTaskType = SPORT_TYPE_TO_CLICK_TASK_TYPE[sportType];
	}

	return Maybe(clickTaskType)
		.map((taskType: ClickTaskTypeEnum) => 
			missions
				.filter((m): m is WcPromotionSpinDailyClickTask => 'clickTaskType' in m)
				.find(m => m.clickTaskType === taskType && !m.isCompleted)
		)
		.chain(Maybe)
		.map((m: WcPromotionSpinDailyClickTask) => m.clickTaskType)
		.fold(
			() => null,
			(type: ClickTaskTypeEnum) => [{
				taskType: 3,
				clickTaskType: type
			}]
		);
};

const resolveBetTaskTypes: CompletableTaskTypeResolver = ({ betTypes, transId }, missions) => {
	if (!betTypes?.length) {
		return null;
	}

	const completableBetTypes = missions
		.filter((m): m is WcPromotionSpinDailyBetTask => 'betType' in m && m.betType != null)
		.filter(m => !m.isCompleted && betTypes.includes(m.betType))
		.map(m => ({
			taskType: 1,
			betType: m.betType,
			transId
		}));

	return completableBetTypes.length ? completableBetTypes : null;
};


const TASK_TYPE_RESOLVER_MAP: Partial<Record<TaskTypeEnum, CompletableTaskTypeResolver>> = {
	[TaskTypeEnum.ClickVirtualSports]: resolveClickTaskTypes,
	[TaskTypeEnum.ClickSabaFeatures]: resolveClickTaskTypes,
	[TaskTypeEnum.MajorBet]: resolveBetTaskTypes,
};

export const useWcPromotionMissionTracker = () => {
	const router = useRouter();
	const queryClient = useQueryClient();
	const t = useTranslations('General');
	const { enableWcPromotionLuckySpin } = useLicSettings();
	const apiBackendInfo = useApiBackendInfo();
	const { isEligibleForWcInteraction } = useWcPromotionEligibility();
	const toastManager = Toast.useToastManager();

	const inTriggeredTasksRef = useRef<Set<string>>(new Set());

	const mutation = useWcPromotionSpinTasksComplete({
		onSuccess: (data: WcPromotionSpinTasksCompleteResponse | null) => {
			if (data?.completedTasks?.length) {
				const missionsResult = queryClient.getQueryData<
					ApiBackendSingleResponse<WcPromotionSpinDailyInfoResponse>
				>([queryKeys.WC_PROMOTION_SPIN_DAILY_INFO, apiBackendInfo.url, apiBackendInfo.token]);
				const missionsData = (missionsResult?.Data ?? [])[0];

				if (!missionsData) return;

				const completedMissions = missionsData.tasks.filter(task => {
					if ('clickTaskType' in task && task.clickTaskType != null) {
						return data.completedTasks.some(
							t => 'clickTaskType' in t && t.clickTaskType === task.clickTaskType
						);
					}
					if ('betType' in task && task.betType != null) {
						return data.completedTasks.some(
							t => 'betType' in t && t.betType === task.betType
						);
					}
					return false;
				});

				if (data.shouldNotify) {
					toastManager.add({
						title: t('lbl_26WCSpinInfo01'),
						description: t('lbl_26WCSpinInfo02'),
						data: {
							style: ToastStyle.WcSpinInfo,
							onClick: () => {
								setKafkaNormal({
									note: KafkaNote.WcPromotion2026,
									type: 1,
									action: 67
								});
								router.replace('/wc-promotion?tab=spin');
							},
						},
					});
				}

				completedMissions.forEach(task => {
					const toastDescription = 'clickTaskType' in task && task.clickTaskType != null
						? getClickTaskToastContentMap(t)[task.clickTaskType]
						: 'betType' in task && task.betType != null
							? getBetTaskToastContentMap(t)[task.betType]
							: undefined;

					toastManager.add({
						title: t('lbl_26WCSpinMissionDone'),
						description: toastDescription,
						data: {
							style: ToastStyle.WcPromotion2026,
							point: task.pointsPerCompletion,
							onClick: () => {
								setKafkaNormal({
									note: KafkaNote.WcPromotion2026,
									type: 1,
									action: 9
								});
								router.replace('/wc-promotion?tab=spin');
							},
						},
					});
				});
			}
		}
	});

	const completeWcMission = async (params: WcMissionPayload) => {
		if (!enableWcPromotionLuckySpin || !isEligibleForWcInteraction) {
			return;
		}

		if (params.betTypes && !params.betTypes.some(betType => WC_PROMOTION_ELIGIBLE_BET_TYPES.has(betType))) {
			return;
		};

		const taskKey = `${params.taskType}_${params.productCode ?? ''}_${params.sportType ?? ''}_${params.betTypes?.join(',') ?? ''}`;
		if (inTriggeredTasksRef.current.has(taskKey)) {
			return;
		}
		inTriggeredTasksRef.current.add(taskKey);

		try {
			// get mission list to check whether missions are completed
			const spinDailyInfo = await queryClient.fetchQuery({ 
				queryKey: [queryKeys.WC_PROMOTION_SPIN_DAILY_INFO, apiBackendInfo.url, apiBackendInfo.token], 
				queryFn: () => wcPromotionSpinService.fetchWcPromotionSpinDailyInfo(apiBackendInfo),
				staleTime: 60 * 1000,
				cacheTime: 2 * 60 * 1000
			});
			const missions = spinDailyInfo.Data?.[0].tasks ?? [];

			if (!missions.length) {
				return;
			}

			const resolver = TASK_TYPE_RESOLVER_MAP[params.taskType];
			const completableTasks = resolver?.(params, missions);

			if (completableTasks?.length) {
				mutation.mutate({ Tasks: completableTasks });
			}
		} catch (error: any) {
			console.error('completeWcMission failed: ', error);
		} finally {
			inTriggeredTasksRef.current.delete(taskKey);
		}
	};

	return { completeWcMission };
};